// src/pages/stock-score-list/index.js
import React, {
    useEffect,
    useState,
    useRef,
    useContext,
    createContext,
    useCallback
} from "react";
import {
    Spin,
    Input,
    Checkbox,
    App,
    Select,
} from "antd";
import { fetchStockScores, SCORE_POLICIES } from "../../repo/stockscore";
import {
    formatScore,
    formatTimestamp,
    isWeekend,
} from "../../utils/common";
import {
    getNextTradingTimestamp,
    isSameTradingDay,
    isTradingWeekday,
    useMarketSession,
} from "../../utils/marketSession";
import { useMediaQuery } from "react-responsive";
import { useNavigate, useLocation } from "react-router-dom";
import TextHighlighter from "./components/highlight-text";
import ShapeBadge from "./components/shape-badge";

// 自定义防抖 hook
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// 创建Context
const StockScoreContext = createContext();

// 创建Context Provider
export function StockScoreProvider({ children }) {
    // 状态管理
    const [isLoading, setIsLoading] = useState(false);
    const [needKeepPos, setNeedKeepPos] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [uiData, setUIData] = useState([]);
    const [pageNum, setPageNum] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [stockFilter, setStockFilter] = useState("");
    const [turnOnly, setTurnOnly] = useState(false);
    const [buyOnly, setBuyOnly] = useState(false);
    const [sellOnly, setSellOnly] = useState(false);
    const [policies, setPolicies] = useState([]);
    const [currentPolicy, setCurrentPolicy] = useState("default");
    const [timestamp, setTimestamp] = useState(() => {
        const ONE_DAY = 86400000;
        const today = new Date();

        let yesterday_milliseconds = today.getTime() - ONE_DAY;
        let yesterday = new Date(yesterday_milliseconds);
        let initialTimestamp = yesterday.getTime();

        const dayOfWeek = yesterday.getDay();
        if (dayOfWeek === 6) {
            initialTimestamp -= ONE_DAY;
        } else if (dayOfWeek === 0) {
            initialTimestamp -= 2 * ONE_DAY;
        }

        return initialTimestamp;
    });
    const [market, setMarket] = useState(null);

    const debouncedStockFilter = useDebounce(stockFilter, 1500);

    // 初始化市场
    const initMarket = useCallback((newMarket) => {
        setMarket(newMarket);
        const marketPolicies = SCORE_POLICIES[newMarket] || [];
        setPolicies(marketPolicies);
        // setCurrentPolicy(marketPolicies[0]?.value || "default");
    }, []);

    // 设置时间戳
    const setTimestampWithParam = useCallback((ts) => {
        setTimestamp(ts);
    }, []);

    // 新增：请求标识ref
    const requestIdRef = useRef(0);

    // 切换市场/日期/策略时先清空旧列表，立即展示 loading
    const clearList = useCallback(() => {
        setIsLoading(true);
        setPageNum(0);
        setDataSource([]);
        setUIData([]);
        setHasMore(false);
    }, []);

    // 首次加载数据（重置加载）
    const loadInitialData = useCallback(async () => {
        if (!market) return;

        clearList();

        // 生成唯一请求ID (时间戳+随机数)
        const currentRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        requestIdRef.current = currentRequestId; // 存储当前请求ID

        try {
            console.log(`start to loadInitialData with policy ${currentPolicy}`)
            const data = await fetchStockScores(
                timestamp,
                market,
                0, // 总是从第0页开始
                currentPolicy,
                debouncedStockFilter,
                turnOnly,
                buyOnly,
                sellOnly
            );

            // 关键修改：校验请求ID是否匹配
            if (requestIdRef.current === currentRequestId) {
                if (data.code === 200) {
                    setNeedKeepPos(true)
                    setDataSource(data.data);
                    setHasMore(data.hasMore);
                    setPageNum(data.pageNum); // 更新当前页码
                }
            } else {
                console.log('忽略过期响应', currentRequestId, requestIdRef.current);
            }

        } catch (error) {
            console.error("加载数据失败:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        market,
        timestamp,
        currentPolicy,
        debouncedStockFilter,
        turnOnly,
        buyOnly,
        sellOnly,
        clearList
    ]);

    // 加载更多数据
    const loadMoreData = useCallback(async () => {
        if (!market || isLoading) return;

        setIsLoading(true);

        // 生成唯一请求ID (时间戳+随机数)
        const currentRequestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        requestIdRef.current = currentRequestId; // 存储当前请求ID

        try {
            // 使用当前页码加载下一页数据
            const data = await fetchStockScores(
                timestamp,
                market,
                pageNum, // 使用当前页码
                currentPolicy,
                debouncedStockFilter,
                turnOnly,
                buyOnly,
                sellOnly
            );


            // 关键修改：校验请求ID是否匹配
            if (requestIdRef.current === currentRequestId) {
                if (data.code === 200) {
                    setNeedKeepPos(false)
                    setDataSource(prev => [...prev, ...data.data]); // 追加数据
                    setHasMore(data.hasMore);
                    setPageNum(data.pageNum); // 更新当前页码
                }
            } else {
                console.log('忽略过期响应', currentRequestId, requestIdRef.current);
            }
        } catch (error) {
            console.error("加载更多数据失败:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        market,
        timestamp,
        currentPolicy,
        debouncedStockFilter,
        turnOnly,
        buyOnly,
        sellOnly,
        pageNum,
        isLoading
    ]);

    // 当过滤条件变化时重新加载数据
    useEffect(() => {
        if (market) {
            loadInitialData();
        }
    }, [
        timestamp,
        market,
        currentPolicy,
        debouncedStockFilter,
        turnOnly,
        buyOnly,
        sellOnly,
        loadInitialData
    ]);

    // 更新UI数据
    useEffect(() => {
        const filterData = dataSource.map(item => {
            const newItem = { ...item };
            if (currentPolicy === "fourier") {
                newItem.score = item.result1;
                newItem.remark = item.description1;
            } else if (currentPolicy === "fourier_new") {
                newItem.score = item.result2;
                newItem.remark = item.description2;
            }
            return newItem;
        });
        setUIData(filterData);
    }, [dataSource, currentPolicy]);

    // 提供上下文值
    const contextValue = {
        isLoading,
        needKeepPos,
        setNeedKeepPos,
        dataSource,
        uiData,
        hasMore,
        stockFilter,
        setStockFilter,
        turnOnly,
        setTurnOnly,
        buyOnly,
        setBuyOnly,
        sellOnly,
        setSellOnly,
        policies,
        currentPolicy,
        setCurrentPolicy,
        timestamp,
        setTimestamp: setTimestampWithParam,
        market,
        initMarket,
        clearList,
        loadInitialData,
        loadMoreData
    };

    return (
        <StockScoreContext.Provider value={contextValue}>
            {children}
        </StockScoreContext.Provider>
    );
}

// 自定义hook访问Context
export const useStockScore = () => useContext(StockScoreContext);

export default function StockScoreList({ market }) {
    // 使用Context
    const {
        isLoading,
        needKeepPos,
        setNeedKeepPos,
        uiData,
        hasMore,
        stockFilter,
        setStockFilter,
        turnOnly,
        setTurnOnly,
        buyOnly,
        setBuyOnly,
        sellOnly,
        setSellOnly,
        policies,
        currentPolicy,
        setCurrentPolicy,
        timestamp,
        setTimestamp,
        initMarket,
        clearList,
        loadMoreData,
        market: loadedMarket
    } = useStockScore();

    // 市场还没切过来之前，先隐藏上一个市场的旧数据，直接显示 loading
    const switchingMarket = Boolean(loadedMarket && loadedMarket !== market);
    const displayData = switchingMarket ? [] : uiData;
    const displayLoading = isLoading || switchingMarket;

    // 初始化市场
    useEffect(() => {
        if (market) {
            initMarket(market);
        }
    }, [market, initMarket]);

    // 处理URL参数中的时间戳
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paramTs = params.get("ts");
        if (paramTs) {
            setTimestamp(parseInt(paramTs));
        }
    }, [location.search, setTimestamp]);

    // 滚动位置管理
    const listContainerRef = useRef(null);
    const isMobile = useMediaQuery({ query: "(max-width: 900px)" });
    const navigate = useNavigate();
    const { message } = App.useApp();
    const session = useMarketSession(market);
    const latestTradingTimestamp = session.latestTradingTimestamp;
    const atLatestTradingDay = formatTimestamp(timestamp) >= formatTimestamp(latestTradingTimestamp);

    // 保存滚动位置到 sessionStorage
    const saveScrollPosition = () => {
        if (listContainerRef.current) {
            const scrollPosition = listContainerRef.current.scrollTop;
            sessionStorage.setItem(
                `stockListScroll_${market}_${timestamp}_${currentPolicy}`,
                scrollPosition.toString()
            );
        }
    };

    // 恢复滚动位置
    const restoreScrollPosition = useCallback(() => {
        const key = `stockListScroll_${market}_${timestamp}_${currentPolicy}`;
        const savedPosition = sessionStorage.getItem(key);
        if (savedPosition && listContainerRef.current) {
            listContainerRef.current.scrollTop = parseInt(savedPosition, 10);
        }
    }, [market, timestamp, currentPolicy]);

    // 恢复滚动位置（当数据加载完成时）
    useEffect(() => {
        if (!isLoading && needKeepPos && uiData.length > 0) {
            setTimeout(restoreScrollPosition, 0);
        }
    }, [isLoading, needKeepPos, uiData, restoreScrollPosition]);

    // 手机端：标题栏 + 表格列标题按滚动吸顶
    const [showBackToTop, setShowBackToTop] = useState(false);

    const handleListScroll = (event) => {
        setShowBackToTop(event.currentTarget.scrollTop > 180);
    };

    const scrollToTop = () => {
        if (listContainerRef.current) {
            listContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // 日期导航
    const onPrevDayClick = () => {
        setNeedKeepPos(false);
        saveScrollPosition();
        clearList();
        let newTimestamp = timestamp - 86400000;
        while (isWeekend(newTimestamp)) {
            newTimestamp -= 86400000;
        }
        navigate(`${location.pathname}?ts=${newTimestamp}`);
    };

    const onNextDayClick = () => {
        if (atLatestTradingDay) {
            return;
        }
        setNeedKeepPos(false);
        saveScrollPosition();
        const newTimestamp = getNextTradingTimestamp(timestamp, latestTradingTimestamp);
        if (isSameTradingDay(newTimestamp, timestamp)) {
            return;
        }
        clearList();
        navigate(`${location.pathname}?ts=${newTimestamp}`);
    };

    // 日期选择：上限为当前市场最近一个交易日，周末不可选
    const onDateChange = (event) => {
        const value = event.target.value;
        if (!value) {
            return;
        }
        const selected = new Date(`${value}T00:00:00`).getTime();
        if (Number.isNaN(selected) || formatTimestamp(selected) > formatTimestamp(latestTradingTimestamp)) {
            return;
        }
        if (!isTradingWeekday(selected)) {
            message.warning(`${session.marketLabel}周末休市，请选择交易日`);
            return;
        }
        setNeedKeepPos(false);
        saveScrollPosition();
        clearList();
        navigate(`${location.pathname}?ts=${selected}`);
    };

    // 点击日期区域任意位置都能呼出日期选择器（原生日期输入的日历图标区域太小）
    const openDatePicker = (event) => {
        const input = event.currentTarget;
        if (typeof input.showPicker === "function") {
            try {
                input.showPicker();
            } catch (_) {
                // 浏览器不允许时保留原生点击行为
            }
        }
    };

    // 加载更多按钮点击处理
    const onLoadMore = useCallback(() => {
        if (isLoading) {
            message.info("正在加载中");
            return;
        }
        if (!hasMore) {
            message.info("没有更多数据了");
            return;
        }

        // 调用加载更多函数
        loadMoreData();
    }, [isLoading, hasMore, loadMoreData, message]);

    // 处理策略切换
    const handlePolicyChange = (value) => {
        setCurrentPolicy(value);
    };

    const filterActive = turnOnly || buyOnly || sellOnly || stockFilter !== "";
    const dateText = formatTimestamp(timestamp);
    const weekdayText = formatTimestamp(timestamp, true).split(" ")[1] || "";
    const weekdayShort = weekdayText.replace("星期", "周");
    const headerButtonStyle = isMobile
        ? { height: 32, padding: "0 10px", fontSize: 10 }
        : undefined;

    const rankingHeading = (
        <div className={isMobile
            ? "mobile-list-heading"
            : "mb-3 flex flex-none items-end justify-between gap-4 px-1"}
        >
            <h2 className="text-[15px] font-medium leading-none tracking-[0.03em] text-[#D8D1C2]">量化收益排名</h2>
            <span className="text-[11px] leading-none text-[#7D786F]">按收益降序</span>
        </div>
    );

    const rankingTableHead = (
        <div className={`table-head ${isMobile ? "" : "flex-none"}`}>
            <span>标的</span>
            <span className="flex items-center gap-1.5">
                收益
                <svg aria-hidden="true" fill="none" height="11" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 24 24" width="11">
                    <path d="M12 5v14M6 13l6 6 6-6" />
                </svg>
            </span>
            <span>形态</span>
        </div>
    );

    return (
        <div
            ref={isMobile ? listContainerRef : null}
            onScroll={isMobile ? handleListScroll : undefined}
            className={`list-mobile-scroll w-full h-full flex flex-col ${isMobile
                ? "overflow-y-auto overflow-x-hidden px-3.5"
                : "overflow-hidden p-5"}`}
        >
            <header className={`mb-3.5 grid flex-none grid-cols-[auto_1fr_auto] items-center gap-2 border-b border-[#D4AF37]/55 pb-4 ${isMobile ? "pt-3.5" : ""}`}>
                <button
                    className="toolbar-button"
                    disabled={atLatestTradingDay || isLoading}
                    onClick={onNextDayClick}
                    style={headerButtonStyle}
                    type="button"
                >
                    <svg aria-hidden="true" fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 24 24" width="14">
                        <path d="m11 17-5-5 5-5M18 17l-5-5 5-5" />
                    </svg>
                    后一天
                </button>
                <div className="min-w-0 text-center">
                    {isMobile ? (
                        <label className="relative inline-flex cursor-pointer select-none flex-col items-center" title="点击选择日期">
                            <span className="flex items-center justify-center gap-1">
                                <span className="font-mono text-[13px] font-semibold tracking-[0.035em] text-[#F3EAD7]">
                                    {dateText}
                                </span>
                                <svg aria-hidden="true" className="text-[#B6A16D]" fill="none" height="9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="9">
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </span>
                            <span className="mt-0.5 flex items-center justify-center gap-1.5 whitespace-nowrap text-[9px] tracking-[0.08em] text-[#9A9489]">
                                {weekdayShort}
                                <span className={`session-pip ${session.status}`} />
                                <span className={`session-status ${session.status}`}>{session.statusText}</span>
                                {isLoading && <span className="ml-1 inline-flex align-middle"><Spin size="small" /></span>}
                            </span>
                            <input
                                type="date"
                                aria-label="选择日期"
                                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                value={dateText}
                                max={formatTimestamp(latestTradingTimestamp)}
                                onClick={openDatePicker}
                                onChange={onDateChange}
                            />
                        </label>
                    ) : (
                        <label className="relative inline-flex cursor-pointer select-none flex-col items-center" title="点击选择日期">
                            <span className="mb-1 flex flex-wrap items-center justify-center gap-2 text-[9px] uppercase tracking-[0.2em] text-[#7A705C]">
                                <span className={`session-pip ${session.status}`} />
                                <span className={`session-status ${session.status}`}>
                                    {session.marketLabel} · {session.statusText}
                                </span>
                                <span className="normal-case tracking-[0.08em] text-[#8C8270]">
                                    {session.rangeText}（{session.timeZoneLabel}）
                                </span>
                                <span className="h-px w-7 bg-[#D4AF37]/25" />
                            </span>
                            <span className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
                                <span className="font-mono text-[21px] font-semibold tracking-[0.08em] text-[#F3EAD7]">
                                    {dateText}
                                </span>
                                <span className="text-[13px] text-[#C8BEA9]">{weekdayText}</span>
                                <svg aria-hidden="true" className="self-center text-[#B6A16D]" fill="none" height="10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="10">
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                                {isLoading && <Spin size="small" />}
                            </span>
                            <input
                                type="date"
                                aria-label="选择日期"
                                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                value={dateText}
                                max={formatTimestamp(latestTradingTimestamp)}
                                onClick={openDatePicker}
                                onChange={onDateChange}
                            />
                        </label>
                    )}
                </div>
                <button
                    className="toolbar-button"
                    disabled={isLoading}
                    onClick={onPrevDayClick}
                    style={headerButtonStyle}
                    type="button"
                >
                    前一天
                    <svg aria-hidden="true" fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 24 24" width="14">
                        <path d="m13 7 5 5-5 5M6 7l5 5-5 5" />
                    </svg>
                </button>
            </header>

            <section aria-label="信号筛选" className={`panel flex-none ${isMobile ? "mb-3.5 px-3.5 py-3" : "mb-5 px-4 py-4"}`}>
                {isMobile ? (
                    <>
                        <div className="flex items-center gap-2.5">
                            <span className="filter-label w-[27px] shrink-0">标的</span>
                            <div className="relative min-w-0 flex-1">
                                <svg aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#756D5C]" fill="none" height="12" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" viewBox="0 0 24 24" width="12">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m20 20-3.4-3.4" />
                                </svg>
                                <Input
                                    aria-label="标的名称或代号"
                                    className="text-[10px]"
                                    onChange={(e) => {
                                        setStockFilter(e.target.value);
                                    }}
                                    placeholder="标的名称or代号"
                                    style={{ width: "100%", height: 31, paddingLeft: 28 }}
                                    value={stockFilter}
                                />
                            </div>
                        </div>
                        <div className="mt-2.5 flex items-center gap-1.5 border-t border-[#E8C37A]/30 pt-2.5">
                            <span className="filter-label w-[27px] shrink-0">形态</span>
                            <div className="flex min-w-0 flex-1 items-center justify-between gap-1">
                                <Checkbox
                                    checked={turnOnly}
                                    className="text-[12px]"
                                    onChange={(e) => setTurnOnly(e.target.checked)}
                                >
                                    只看反转
                                </Checkbox>
                                <Checkbox
                                    checked={buyOnly}
                                    className="text-[12px]"
                                    onChange={(e) => setBuyOnly(e.target.checked)}
                                >
                                    只看多
                                </Checkbox>
                                <Checkbox
                                    checked={sellOnly}
                                    className="text-[12px]"
                                    onChange={(e) => setSellOnly(e.target.checked)}
                                >
                                    只看空
                                </Checkbox>
                            </div>
                        </div>
                        <div className="mt-2.5 flex items-center gap-2.5 border-t border-[#E8C37A]/30 pt-2.5">
                            <span className="filter-label w-[27px] shrink-0">策略</span>
                            <Select
                                className="mobile-filter-select"
                                onChange={handlePolicyChange}
                                options={policies}
                                style={{ width: 132 }}
                                value={currentPolicy}
                            />
                            {filterActive && (
                                <button
                                    className="text-button ml-auto text-[9px] text-[#D6BC7A] underline"
                                    onClick={onLoadMore}
                                    type="button"
                                >
                                    {hasMore ? "加载更多" : "已全部加载"}
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                        <div className="flex items-center gap-3">
                            <span className="filter-label">标的</span>
                            <Input
                                aria-label="标的名称或代号"
                                className="text-[13px]"
                                onChange={(e) => {
                                    setStockFilter(e.target.value);
                                }}
                                placeholder="标的名称or代号"
                                style={{ width: 244, height: 38 }}
                                value={stockFilter}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <span className="filter-label">形态</span>
                            <Checkbox
                                checked={turnOnly}
                                onChange={(e) => setTurnOnly(e.target.checked)}
                            >
                                只看反转
                            </Checkbox>
                            <Checkbox
                                checked={buyOnly}
                                onChange={(e) => setBuyOnly(e.target.checked)}
                            >
                                只看多
                            </Checkbox>
                            <Checkbox
                                checked={sellOnly}
                                onChange={(e) => setSellOnly(e.target.checked)}
                            >
                                只看空
                            </Checkbox>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="filter-label">策略</span>
                            <Select
                                onChange={handlePolicyChange}
                                options={policies}
                                style={{ width: 170 }}
                                value={currentPolicy}
                            />
                        </div>
                        <div className="ml-auto flex items-center gap-3 text-[10px] tracking-[0.12em] text-[#726C5E]">
                            {filterActive && (
                                <button
                                    className="text-button text-[#D6BC7A] underline"
                                    onClick={onLoadMore}
                                    type="button"
                                >
                                    {hasMore ? "前端筛选，点击加载更多" : "都在这里了"}
                                </button>
                            )}
                            <span className="hidden items-center gap-2 xl:flex">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]/70 shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
                                量价因子雷达已同步
                            </span>
                        </div>
                    </div>
                )}
            </section>

            {!isMobile && rankingHeading}

            <div className={`signal-table ${isMobile ? "signal-table-mobile" : "flex min-h-0 flex-1 flex-col"}`}>
                {isMobile ? (
                    <div className="mobile-list-sticky">
                        {rankingHeading}
                        {rankingTableHead}
                    </div>
                ) : (
                    rankingTableHead
                )}
                <div
                    ref={isMobile ? null : listContainerRef}
                    className={isMobile ? "signal-table-rows" : "min-h-0 flex-1 overflow-auto"}
                >
                    {displayData.length === 0 && !displayLoading && (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <svg aria-hidden="true" fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" viewBox="0 0 24 24" width="16">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m20 20-3.4-3.4" />
                                </svg>
                            </div>
                            <p className="text-[13px] text-[#D2CBBE]">暂无数据</p>
                            <p className="mt-1 text-[10px] text-[#74716A]">最新数据将于次日中午12点前更新</p>
                        </div>
                    )}
                    {displayData.length === 0 && displayLoading && (
                        <div className="empty-state">
                            <Spin />
                            <p className="mt-3 text-[11px] text-[#858179]">正在加载量化信号...</p>
                        </div>
                    )}
                    {displayData.map((item) => (
                        <div
                            className="data-row"
                            key={item.id}
                            onClick={() => {
                                setNeedKeepPos(true)
                                saveScrollPosition();
                                navigate(
                                    `/score/detail?code=${item.code}&policy=${currentPolicy}&market=${market}`
                                );
                            }}
                        >
                            <div className="flex min-w-0 flex-col items-start gap-0">
                                <TextHighlighter
                                    className="signal-code"
                                    highlight={stockFilter}
                                    text={item.code}
                                />
                                <TextHighlighter
                                    className="signal-name"
                                    highlight={stockFilter}
                                    text={item.name}
                                />
                            </div>
                            <span className="score-number">
                                <span className="score-value">{formatScore(item.score)}</span>
                            </span>
                            <span className="flex items-center justify-between gap-2">
                                <ShapeBadge remark={item.remark} />
                                {!isMobile && (
                                    <svg aria-hidden="true" className="row-arrow shrink-0 text-[#B9A675]" fill="none" height="13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24" width="13">
                                        <path d="M5 12h14M13 6l6 6-6 6" />
                                    </svg>
                                )}
                            </span>
                        </div>
                    ))}
                    {displayData.length > 0 && hasMore && !displayLoading && (
                        <div className="load-more-wrap">
                            <button className="toolbar-button" onClick={onLoadMore} type="button">
                                点击加载更多
                            </button>
                        </div>
                    )}
                    {displayLoading && displayData.length > 0 && (
                        <div className="load-more-wrap">
                            <Spin />
                        </div>
                    )}
                </div>
            </div>
            {isMobile && showBackToTop && (
                <button
                    aria-label="回到列表顶部"
                    className="list-back-to-top"
                    onClick={scrollToTop}
                    type="button"
                >
                    <svg aria-hidden="true" fill="none" height="18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="18">
                        <path d="M12 19V5M6 11l6-6 6 6" />
                    </svg>
                </button>
            )}
        </div>
    );
}
